import { Languages } from '@platform/client';
import { OrganizationApplicationParameters } from '@platform/client/schemas';
import { InlineFormInput, MultiSelectDropdown } from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import BackgroundImage from '../../images/Leftsection11.svg';

type FormValues = {
  repJobTitle: string;
  repLinkedin: string;
  repLanguages?: string[];
};

export const OrgRepBackgroundSelection = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress } = { ...props };

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<OrganizationApplicationParameters>();

  const languages = Languages.useGetLanguages();
  const languageOptions =
    languages.isSuccess ?
      languages.data.flatMap((lang) => {
        if (lang.name === 'English') {
          return [];
        }
        return [
          {
            label: lang.name,
          },
        ];
      })
    : [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      repJobTitle: getApplicationDataByKey('repJobTitle'),
      repLinkedin: getApplicationDataByKey('repLinkedin'),
      repLanguages: getApplicationDataByKey('repLanguages') || [],
    },
  });

  const submit: SubmitHandler<FormValues> = (data) => {
    updateApplicationData({
      repJobTitle: data.repJobTitle,
      repLinkedin: data.repLinkedin,
      repLanguages: data.repLanguages || [],
    });
    onContinue();
  };

  const onSelectionChange = (value: string[]) => {
    setValue('repLanguages', value);
  };

  const handleBack = () => {
    updateApplicationData({
      repJobTitle: watch('repJobTitle'),
      repLinkedin: watch('repLinkedin'),
      repLanguages: watch('repLanguages'),
    });
    onBack();
  };

  return (
    <ApplicationBackground
      progress={progress}
      isOrganization={true}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      backgroundImage={BackgroundImage}
      isEditProfile={props.isEditProfile}
    >
      <ApplicationPageContent
        title='Tell us a bit more about your professional background'
        description='
          This information will be visible on your personal profile, and can be updated anytime.'
      >
        <InlineFormInput
          label='Current role in your organization'
          placeholder='Enter your current job title'
          register={() =>
            register('repJobTitle', {
              required: 'Enter your job title in your organization to continue',
            })
          }
          errors={errors.repJobTitle}
        />
        <InlineFormInput
          label='Personal LinkedIn (optional)'
          placeholder='Enter LinkedIn URL'
          prefixContent='http://'
          register={() => register('repLinkedin')}
          errors={errors.repLinkedin}
        />
        <MultiSelectDropdown
          register={() => {
            return register('repLanguages');
          }}
          selectedItems={watch('repLanguages') || []}
          onSelectionChange={onSelectionChange}
          options={languageOptions}
          placeholder='Start typing languages'
          label='Other than English, what languages are you comfortable working in? (optional)'
          error={errors.repLanguages?.message}
          filterable={true}
        />
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
